import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import TextInput from '../forms/formComponents/TextInput';
import { CHOOSE_OPTION_LABEL, getFormFieldPlaceholder } from '../../utils/formFieldPresentation';
import SubmitButton from '../forms/formComponents/SubmitButton';
import ErrorMessage from '../forms/formComponents/ErrorMessage';
import ParentEmergencySyncDetailsModal from '../forms/ParentEmergencySyncDetailsModal';
import EmergencyContactModal from './EmergencyContactModal';
import { useJoin } from './joinContext';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../../firebase-client';
import { getDocumentActionLabels, getDocumentSourceUrl, triggerDocumentDownload } from '../teams/utils/documentUtils';
import { buildRequiredTagGroups, getPricingTagNames, getMissingRequiredTagGroups } from '../../utils/requiredTags';
const PRICING_QUESTION_IDS = ['pricingTags', 'pricingTag'];
const isPricingQuestion = q => PRICING_QUESTION_IDS.includes(q?.id);
const syncEmergencyContactsFromParentFn = httpsCallable(functions, 'syncEmergencyContactsFromParent');
function normalizeQuestionValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value;
  return String(value);
}
function normalizeDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function resolveAgeRuleTags(ageRuleTags = [], dob = null) {
  if (!dob || !Array.isArray(ageRuleTags) || ageRuleTags.length === 0) return [];
  const result = new Set();
  for (const ageRuleTag of ageRuleTags) {
    const monthIndex = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].indexOf(ageRuleTag?.effectiveDate?.month);
    if (monthIndex < 0) continue;
    const refDate = new Date();
    refDate.setMonth(monthIndex);
    refDate.setDate(ageRuleTag?.effectiveDate?.day || 1);
    const ageAtRef = refDate.getFullYear() - dob.getFullYear() - (refDate < new Date(refDate.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
    const applies = ageRuleTag?.rule === 'under' ? ageAtRef < Number(ageRuleTag?.age) : ageAtRef >= Number(ageRuleTag?.age);
    if (!applies) continue;
    const ruleTagNames = Array.isArray(ageRuleTag?.tagNames) ? ageRuleTag.tagNames : [ageRuleTag?.tagName];
    ruleTagNames.filter(name => typeof name === 'string' && name.trim() !== '').forEach(name => result.add(name.trim()));
  }
  return Array.from(result);
}
function normalizeTagNames(tags = []) {
  return Array.from(new Set((Array.isArray(tags) ? tags : []).map(tag => typeof tag === 'string' ? tag.trim() : '').filter(Boolean)));
}
export default function JoinForm() {
  const {
    questions,
    team,
    user,
    setStatus,
    applyJoinPricingTags,
    error,
    membershipPlanType,
    selectedFamilyTierId,
    selectedFamilyTier,
    familyMembershipGroups,
    selectedFamilyId,
    selectedIndividualTargetFamilyId,
    membershipTargetUserIds,
    familySelectionValidation,
    membershipTargetCount,
    membershipPricingTag,
    preassignedTags,
    requiredJoinDocuments,
    isDemoMode,
    waitlistEnabled,
    isPrelaunchCommitteeMode
  } = useJoin();
  const [loading, setLoading] = useState(true);
  const [pricingTagOptions, setPricingTagOptions] = useState([]);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [memberFormResponses, setMemberFormResponses] = useState({});
  const [requiredTagsByMember, setRequiredTagsByMember] = useState({});
  const [acceptedDocuments, setAcceptedDocuments] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [emergencyContactModalOpen, setEmergencyContactModalOpen] = useState(false);
  const [syncDetailsModalOpen, setSyncDetailsModalOpen] = useState(false);
  const activeFamilyGroup = membershipPlanType === 'individual'
    ? familyMembershipGroups.find(group => group.familyId === selectedIndividualTargetFamilyId) || null
    : familyMembershipGroups.find(group => group.familyId === selectedFamilyId) || familyMembershipGroups[0] || null;
  const activeFamilyMembers = useMemo(() => Array.isArray(activeFamilyGroup?.members) ? activeFamilyGroup.members : [], [activeFamilyGroup]);
  const targetMemberIds = useMemo(() => {
    if (!user?.uid) return [];
    return Array.isArray(membershipTargetUserIds) && membershipTargetUserIds.length > 0 ? membershipTargetUserIds : [user.uid];
  }, [user?.uid, membershipTargetUserIds]);
  const targetApplicants = useMemo(() => {
    if (!user?.uid) return [];
    return targetMemberIds.map(uid => {
      const familyMember = activeFamilyMembers.find(member => member.uid === uid);
      if (familyMember) {
        return {
          uid,
          role: familyMember.role || null,
          displayName: familyMember.name || (uid === user.uid ? 'You' : 'Family member'),
          isPayer: uid === user.uid
        };
      }
      return {
        uid,
        role: uid === user.uid ? 'parent' : null,
        displayName: uid === user.uid ? 'You' : 'Family member',
        isPayer: uid === user.uid
      };
    }).filter(item => Boolean(item.uid));
  }, [user?.uid, targetMemberIds, activeFamilyMembers]);
  const normalizedPreassignedTags = useMemo(() => normalizeTagNames(preassignedTags), [preassignedTags]);
  const isAdminDemo = Boolean(isDemoMode && user?.uid && Array.isArray(team?.admins) && team.admins.includes(user.uid));
  const realQuestions = useMemo(() => Array.isArray(questions) ? questions.filter(question => !isPricingQuestion(question)) : [], [questions]);
  useEffect(() => {
    const requiredDocs = Array.isArray(requiredJoinDocuments) ? requiredJoinDocuments : [];
    setAcceptedDocuments(prev => {
      const next = {};
      requiredDocs.forEach(doc => {
        if (!doc?.id) return;
        next[doc.id] = prev?.[doc.id] === true;
      });
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length && prevKeys.every(key => next[key] === prev[key])) {
        return prev;
      }
      return next;
    });
  }, [requiredJoinDocuments]);
  useEffect(() => {
    if (!Array.isArray(targetMemberIds) || targetMemberIds.length === 0) {
      setMemberFormResponses({});
      setRequiredTagsByMember({});
      return;
    }
    setMemberFormResponses(prev => {
      const next = {};
      targetMemberIds.forEach(uid => {
        next[uid] = {
          ...(prev?.[uid] || {})
        };
      });
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length && prevKeys.every(key => next[key] && JSON.stringify(prev[key]) === JSON.stringify(next[key]))) {
        return prev;
      }
      return next;
    });
    setRequiredTagsByMember(prev => {
      const next = {};
      targetMemberIds.forEach(uid => {
        next[uid] = {
          ...(prev?.[uid] || {})
        };
      });
      return next;
    });
  }, [targetMemberIds]);
  const checkOwnEmergencyContacts = useCallback(async ({
    openModal = true
  } = {}) => {
    if (!user?.uid) return false;
    try {
      const snap = await getDoc(doc(db, 'privateUsers', user.uid));
      const hasContacts = snap.exists() && Array.isArray(snap.data().emergencyContacts) && snap.data().emergencyContacts.length > 0;
      if (!hasContacts && openModal) {
        setEmergencyContactModalOpen(true);
      }
      return hasContacts;
    } catch (err) {
      if (openModal) {
        setSubmitError('Could not check your emergency contact details. Check your connection and try again.');
      }
      return false;
    }
  }, [user?.uid]);
  useEffect(() => {
    let cancelled = false;
    const loadJoinFormState = async () => {
      if (!team?.id || !user?.uid) return;
      try {
        const [stripeSnap, hasContacts] = await Promise.all([
          getDoc(doc(db, `teams/${team.id}/stripe/setup`)).catch(() => null),
          checkOwnEmergencyContacts({
            openModal: false
          })
        ]);
        if (cancelled) return;
        if (stripeSnap?.exists()) {
          setStripeConfig(stripeSnap.data());
        }
        setEmergencyContactModalOpen(!hasContacts);
      } catch (err) {
        if (cancelled) return;
        setSubmitError('Could not load your join details. Check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadJoinFormState();
    return () => {
      cancelled = true;
    };
  }, [checkOwnEmergencyContacts, user?.uid, team?.id]);
  useEffect(() => {
    const pricingTags = [...Object.keys(stripeConfig?.tagOverrides || {}), ...Object.keys(stripeConfig?.tagOverridesYearly || {})].map(name => typeof name === 'string' ? name.trim() : '').filter(Boolean);
    if (pricingTags.length === 0) {
      setPricingTagOptions([]);
      return;
    }
    const allTags = Array.isArray(team?.tags) ? team.tags : [];
    const tagsByName = new Map(allTags.filter(tag => tag?.type !== 'ageRule' && typeof tag?.name === 'string' && tag.name.trim() !== '').map(tag => [tag.name, tag]));
    const pricingCategory = stripeConfig?.pricingCategory;
    const optionsFromOverrides = pricingTags.map(tagName => tagsByName.get(tagName) || {
      name: tagName,
      selfAssignable: false
    });
    const categoryFiltered = optionsFromOverrides.filter(tag => !pricingCategory || tag.category === pricingCategory);
    const pricingOptions = categoryFiltered.length > 0 ? categoryFiltered : optionsFromOverrides;
    setPricingTagOptions(pricingOptions);
  }, [team?.tags, stripeConfig]);
  const lockedPricingTagName = useMemo(() => {
    if (pricingTagOptions.length === 0 || normalizedPreassignedTags.length === 0) return '';
    const byLowerName = new Map(pricingTagOptions.map(tag => typeof tag?.name === 'string' ? tag.name.trim() : '').filter(Boolean).map(name => [name.toLowerCase(), name]));
    for (const tagName of normalizedPreassignedTags) {
      const match = byLowerName.get(tagName.toLowerCase());
      if (match) return match;
    }
    return '';
  }, [pricingTagOptions, normalizedPreassignedTags]);
  const selectedMembershipPricingLabel = lockedPricingTagName || (typeof membershipPricingTag === 'string' ? membershipPricingTag.trim() : '');
  const requiredTagGroups = useMemo(() => buildRequiredTagGroups(team?.tags || [], {
    pricingTagNames: getPricingTagNames(stripeConfig || {})
  }), [stripeConfig, team?.tags]);
  const requiredNonPricingGroups = useMemo(() => requiredTagGroups.filter(group => !group.isPricingGroup), [requiredTagGroups]);
  const handleMemberQuestionChange = (memberUid, questionId, value) => {
    setMemberFormResponses(prev => ({
      ...prev,
      [memberUid]: {
        ...(prev?.[memberUid] || {}),
        [questionId]: value
      }
    }));
  };
  const handleRequiredTagToggle = (memberUid, group, tagName) => {
    setSubmitError('');
    setRequiredTagsByMember(prev => {
      const existing = Array.isArray(prev?.[memberUid]?.[group.category]) ? prev[memberUid][group.category] : [];
      const selected = existing.includes(tagName);
      const nextForGroup = group.multiAssignable
        ? selected ? existing.filter(name => name !== tagName) : [...existing, tagName]
        : selected ? [] : [tagName];
      return {
        ...prev,
        [memberUid]: {
          ...(prev?.[memberUid] || {}),
          [group.category]: nextForGroup
        }
      };
    });
  };
  const renderRequiredTagGroups = memberUid => {
    if (requiredNonPricingGroups.length === 0) return null;
    return requiredNonPricingGroups.map(group => <fieldset key={`${memberUid}-required-${group.category}`} className="tag-category" style={{
      marginBottom: '0.75rem'
    }}>
        <legend>
          {group.category} (required)
          {!group.multiAssignable ? ' - choose one' : ''}
        </legend>
        <div className="tag-options" style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem'
      }}>
          {group.tags.map(tagName => {
          const selected = requiredTagsByMember?.[memberUid]?.[group.category]?.includes(tagName);
          return <label key={`${memberUid}:${group.category}:${tagName}`} className={`tag-pill ${selected ? 'selected' : ''}`}>
                <input type="checkbox" checked={Boolean(selected)} onChange={() => handleRequiredTagToggle(memberUid, group, tagName)} />
                {tagName}
              </label>;
        })}
        </div>
      </fieldset>);
  };
  const renderQuestionInput = (memberUid, question) => {
    const rawValue = memberFormResponses?.[memberUid]?.[question.id];
    const value = normalizeQuestionValue(rawValue);
    if (isPricingQuestion(question)) {
      if (pricingTagOptions.length === 0) return null;
      return <div key={`${memberUid}-${question.id}`} className="form-group">
          <label>
            {question.label}
            {membershipPlanType === 'family_tier' ? ' (for profile/admin review only)' : ''}
          </label>
          <select value={typeof value === 'string' ? value : ''} onChange={e => handleMemberQuestionChange(memberUid, question.id, e.target.value)} required={question.required} className="form-input">
            <option value="">{CHOOSE_OPTION_LABEL}</option>
            {pricingTagOptions.map(tag => <option key={tag.name} value={tag.name}>
                {tag.name || 'Unnamed option'}{!tag.selfAssignable ? ' (requires approval)' : ''}
              </option>)}
          </select>
        </div>;
    }
    if (question.type === 'textarea') {
      return <div key={`${memberUid}-${question.id}`} className="form-group">
          <label>{question.label}</label>
          <textarea className="form-input" value={typeof value === 'string' ? value : ''} onChange={e => handleMemberQuestionChange(memberUid, question.id, e.target.value)} required={question.required} placeholder={getFormFieldPlaceholder(question)} />
        </div>;
    }
    if (question.type === 'number') {
      return <div key={`${memberUid}-${question.id}`} className="form-group">
          <label>{question.label}</label>
          <input type="number" className="form-input" value={typeof value === 'string' ? value : ''} onChange={e => handleMemberQuestionChange(memberUid, question.id, e.target.value)} required={question.required} placeholder={getFormFieldPlaceholder(question)} />
        </div>;
    }
    if (question.type === 'date') {
      return <div key={`${memberUid}-${question.id}`} className="form-group">
          <label>{question.label}</label>
          <input type="date" className="form-input" value={typeof value === 'string' ? value : ''} onChange={e => handleMemberQuestionChange(memberUid, question.id, e.target.value)} required={question.required} />
        </div>;
    }
    if (question.type === 'select') {
      const options = Array.isArray(question.options) ? question.options : [];
      return <div key={`${memberUid}-${question.id}`} className="form-group">
          <label>{question.label}</label>
          <select value={typeof value === 'string' ? value : ''} onChange={e => handleMemberQuestionChange(memberUid, question.id, e.target.value)} required={question.required} className="form-input">
            <option value="">{CHOOSE_OPTION_LABEL}</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>;
    }
    if (question.type === 'yesno') {
      return <div key={`${memberUid}-${question.id}`} className="form-group">
          <label>{question.label}</label>
          <select value={typeof value === 'string' ? value : ''} onChange={e => handleMemberQuestionChange(memberUid, question.id, e.target.value)} required={question.required} className="form-input">
            <option value="">{CHOOSE_OPTION_LABEL}</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>;
    }
    if (question.type === 'checkbox') {
      const checked = rawValue === true || value === 'true' || value === 'yes';
      return <div key={`${memberUid}-${question.id}`} className="form-group">
          <label style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'flex-start'
        }}>
            <input type="checkbox" checked={checked} onChange={e => handleMemberQuestionChange(memberUid, question.id, e.target.checked)} required={question.required} style={{
            marginTop: '0.2rem'
          }} />
            <span>{question.label}</span>
          </label>
        </div>;
    }
    return <TextInput key={`${memberUid}-${question.id}`} label={question.label} value={typeof value === 'string' ? value : ''} required={question.required} onChange={e => handleMemberQuestionChange(memberUid, question.id, e.target.value)} placeholder={getFormFieldPlaceholder(question)} />;
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitError('');
    if (isAdminDemo) {
      alert('Demo mode is read-only. Submission is disabled.');
      return;
    }
    if (membershipPlanType === 'family_tier' && familySelectionValidation) {
      alert(familySelectionValidation);
      return;
    }
    const hasOwnEmergencyContacts = await checkOwnEmergencyContacts();
    if (!hasOwnEmergencyContacts) return;
    const membershipTargetsForSave = targetMemberIds;
    const uniqueTargetIds = Array.from(new Set(membershipTargetsForSave.map(uid => typeof uid === 'string' ? uid.trim() : '').filter(Boolean)));
    if (uniqueTargetIds.length === 0) {
      alert('Please select at least one member.');
      return;
    }
    const requiredDocs = Array.isArray(requiredJoinDocuments) ? requiredJoinDocuments : [];
    const missingAcceptedDocs = requiredDocs.filter(doc => doc?.id && acceptedDocuments?.[doc.id] !== true);
    if (missingAcceptedDocs.length > 0) {
      setSubmitError('You must accept all required club documents before continuing.');
      return;
    }
    const allTags = team.tags || [];
    const ageRuleTags = allTags.filter(tag => tag.type === 'ageRule');
    const selectedMembershipPricingTag = lockedPricingTagName || (typeof membershipPricingTag === 'string' ? membershipPricingTag.trim() : '');
    let payerPrivateSnap;
    let payerPublicSnap;
    let targetSnapshots;
    try {
      [payerPrivateSnap, payerPublicSnap, targetSnapshots] = await Promise.all([getDoc(doc(db, 'privateUsers', user.uid)), getDoc(doc(db, 'users', user.uid)), Promise.all(uniqueTargetIds.map(async uid => {
        const [publicSnap, privateSnap] = await Promise.all([getDoc(doc(db, 'users', uid)).catch(() => null), uid === user.uid ? getDoc(doc(db, 'privateUsers', uid)).catch(() => null) : Promise.resolve(null)]);
        return {
          uid,
          publicData: publicSnap?.exists() ? publicSnap.data() || {} : {},
          privateData: privateSnap?.exists() ? privateSnap.data() || {} : {}
        };
      }))]);
    } catch (err) {
      setSubmitError('Could not load your profile details. Check your connection and try again.');
      return;
    }
    const nonPayerTargetIds = uniqueTargetIds.filter(uid => uid !== user.uid);
    if (nonPayerTargetIds.length > 0) {
      try {
        const checkResult = await syncEmergencyContactsFromParentFn({
          mode: 'check',
          familyId: activeFamilyGroup?.familyId || selectedFamilyId || null,
          targetUserIds: nonPayerTargetIds
        });
        const checkData = checkResult?.data || {};
        if (
          checkData.missingParentPhone === true
          || checkData.missingParentAddress === true
          || checkData.missingParentContacts === true
        ) {
          setSyncDetailsModalOpen(true);
          return;
        }
        const missingIds = Array.isArray(checkData.missingEmergencyContactUserIds) ? checkData.missingEmergencyContactUserIds : [];
        if (missingIds.length > 0) {
          const missingNames = missingIds.map(uid => targetApplicants.find(entry => entry.uid === uid)?.displayName || 'Family member').join(', ');
          const shouldCopy = window.confirm(`${missingNames} ${missingIds.length > 1 ? 'do' : 'does'} not include your details as an emergency contact yet. Sync yourself now?`);
          if (!shouldCopy) return;
          const copyResult = await syncEmergencyContactsFromParentFn({
            mode: 'copy',
            familyId: activeFamilyGroup?.familyId || selectedFamilyId || null,
            targetUserIds: missingIds
          });
          const copyData = copyResult?.data || {};
          const failedIds = Array.isArray(copyData.failedTargetUids) ? copyData.failedTargetUids : [];
          if (failedIds.length > 0) {
            alert('Could not copy emergency contacts for one or more selected family members.');
            return;
          }
        }
      } catch (copyErr) {
        alert(copyErr?.message || 'Could not verify emergency contacts for selected family members.');
        return;
      }
    }
    let requiredTagValidationError = '';
    const targetDetails = targetSnapshots.map(snapshot => {
      const targetUid = snapshot.uid;
      const isPayerTarget = targetUid === user.uid;
      const memberMeta = targetApplicants.find(entry => entry.uid === targetUid) || {};
      const memberResponses = memberFormResponses?.[targetUid] || {};
      const dobRaw = snapshot.privateData?.dob || snapshot.publicData?.dob || null;
      const dob = normalizeDate(dobRaw);
      const requiredSelectedTags = Object.values(requiredTagsByMember?.[targetUid] || {}).flat().filter(Boolean);
      const missingRequired = getMissingRequiredTagGroups(requiredNonPricingGroups, requiredSelectedTags, {
        includePricing: false
      });
      if (missingRequired.length > 0) {
        requiredTagValidationError = requiredTagValidationError || `Choose a tag in ${missingRequired[0].category} for ${memberMeta.displayName || 'Member'}.`;
      }
      const appliedTagSet = new Set([...resolveAgeRuleTags(ageRuleTags, dob), ...(isPayerTarget ? normalizedPreassignedTags : []), ...requiredSelectedTags]);
      const requestedTags = [];
      const selectedPricingTags = membershipPlanType === 'individual' && selectedMembershipPricingTag ? [selectedMembershipPricingTag] : [];
      if (selectedPricingTags.length > 0) {
        selectedPricingTags.forEach(selectedTag => {
          const tagMeta = pricingTagOptions.find(tag => tag.name === selectedTag);
          if (lockedPricingTagName && tagMeta?.name === lockedPricingTagName) {
            appliedTagSet.add(tagMeta.name);
          } else if (tagMeta?.name) {
            appliedTagSet.add(tagMeta.name);
          } else if (typeof selectedTag === 'string' && selectedTag.trim() !== '') {
            appliedTagSet.add(selectedTag.trim());
          }
        });
      }
      const normalizedFormResponses = realQuestions.map(question => ({
        id: question.id,
        label: question.label,
        value: question.type === 'checkbox' ? memberResponses[question.id] === true ? 'yes' : 'no' : normalizeQuestionValue(memberResponses[question.id]),
        type: question.type || 'text',
        syncToProfile: question.syncToProfile === true
      }));
      return {
        uid: targetUid,
        displayName: memberMeta.displayName || snapshot.publicData?.username || `${snapshot.publicData?.firstName || ''} ${snapshot.publicData?.lastName || ''}`.trim() || 'Member',
        role: memberMeta.role || null,
        isPayer: targetUid === user.uid,
        formResponses: normalizedFormResponses,
        appliedTags: Array.from(appliedTagSet),
        requestedTags: Array.from(new Set(requestedTags))
      };
    });
    if (requiredTagValidationError) {
      setSubmitError(requiredTagValidationError);
      return;
    }
    const payerTarget = targetDetails.find(detail => detail.uid === user.uid) || targetDetails[0] || {
      formResponses: [],
      appliedTags: [],
      requestedTags: []
    };
    const hasRequestedTags = targetDetails.some(detail => Array.isArray(detail.requestedTags) && detail.requestedTags.length > 0);
    const documentAcceptances = requiredDocs.map(doc => ({
      documentId: doc.id,
      title: doc.title || '',
      versionLabel: doc.versionLabel || '',
      sourceUrl: getDocumentSourceUrl(doc),
      acceptedAt: new Date().toISOString()
    }));
    const formData = {
      uid: user.uid,
      displayName: payerPublicSnap.exists() ? payerPublicSnap.data().username || '' : '',
      email: payerPrivateSnap.exists() ? payerPrivateSnap.data().email || '' : '',
      phone: payerPrivateSnap.exists() ? payerPrivateSnap.data().phone || '' : '',
      firstName: payerPublicSnap.exists() ? payerPublicSnap.data().firstName || '' : '',
      lastName: payerPublicSnap.exists() ? payerPublicSnap.data().lastName || '' : '',
      dob: payerPublicSnap.exists() ? payerPublicSnap.data().dob || '' : '',
      formResponses: payerTarget.formResponses,
      appliedTags: payerTarget.appliedTags,
      requestedTags: payerTarget.requestedTags,
      membershipTargetDetails: targetDetails,
      membershipPlanType,
      membershipPlanTierId: membershipPlanType === 'family_tier' ? selectedFamilyTier?.id || selectedFamilyTierId || null : null,
      membershipTargetUserIds: uniqueTargetIds,
      membershipTargetFamilyId: uniqueTargetIds.some(uid => uid !== user.uid)
        ? (membershipPlanType === 'individual' ? selectedIndividualTargetFamilyId || null : activeFamilyGroup?.familyId || selectedFamilyId || null)
        : null,
      documentAcceptances,
      preassignedTags: normalizedPreassignedTags,
      membershipPricingTag: selectedMembershipPricingTag || '',
      createdAt: serverTimestamp()
    };
    try {
      const requiresApproval = !isPrelaunchCommitteeMode && team.availability?.joinable === 'On Approval';
      const shouldWaitForApproval = !isPrelaunchCommitteeMode && (requiresApproval || hasRequestedTags);
      const effectiveWaitlistEnabled = !isPrelaunchCommitteeMode && waitlistEnabled;
      const pendingRef = doc(db, 'teams', team.id, 'pendingMembers', user.uid);
      applyJoinPricingTags?.(payerTarget.appliedTags || [], {
        memberCount: membershipTargetCount,
        planType: membershipPlanType,
        planTierId: selectedFamilyTier?.id || selectedFamilyTierId || null
      });
      if (shouldWaitForApproval) {
        await setDoc(pendingRef, {
          ...formData,
          status: 'waiting'
        }, {
          merge: true
        });
        setStatus('waiting');
      } else {
        await setDoc(pendingRef, {
          ...formData,
          status: effectiveWaitlistEnabled ? 'waitlisted' : 'approved',
          approvedAt: serverTimestamp(),
          waitlistPriority: null,
          waitlistedAt: effectiveWaitlistEnabled ? serverTimestamp() : null,
          releasedFromWaitlistAt: null
        }, {
          merge: true
        });
        setStatus(effectiveWaitlistEnabled ? 'waitlisted' : 'payment');
      }
    } catch (err) {
      setSubmitError(err?.message || 'Failed to submit join request.');
    }
  };
  if (loading) return <p>Checking emergency contact details…</p>;
  return <>
    <form onSubmit={handleSubmit} className="form-stacked space-y-4 text-left">
      <div>
        <h2 className="join-flow-section-title">Details and documents</h2>
        <p className="join-flow-section-copy">
          Confirm the selected membership, answer any club questions, and accept required documents before continuing.
        </p>
      </div>

      <div className="form-group">
        <label>Selected Membership</label>
        <p className="text-muted" style={{
        marginTop: '0.25rem'
      }}>
          {membershipPlanType === 'family_tier'
            ? `${selectedFamilyTier?.label || 'Family Membership'} for ${targetMemberIds.length} member(s)`
            : `Individual Membership${selectedMembershipPricingLabel ? ` · ${selectedMembershipPricingLabel}` : ' · Default pricing'}`}
        </p>
        <div style={{
        marginTop: '0.5rem'
      }}>
          <button type="button" className="button button-secondary" onClick={() => setStatus('membership_type')}>
            Change Membership Type
          </button>
        </div>
        {normalizedPreassignedTags.length > 0 && <p className="text-muted" style={{
        marginTop: '0.5rem'
      }}>
            Admin-assigned tags: {normalizedPreassignedTags.join(', ')} (locked)
          </p>}
        {membershipPlanType === 'family_tier' && familySelectionValidation && <p className="text-error" style={{
        marginTop: '0.5rem'
      }}>{familySelectionValidation}</p>}
      </div>

      {targetApplicants.length > 0 && (realQuestions.length > 0 || requiredNonPricingGroups.length > 0) && <div className="form-group">
          <label>{targetApplicants.length > 1 ? 'Member Details' : 'Your Details'}</label>
          <p className="text-muted" style={{
        marginTop: '0.25rem'
      }}>
            {targetApplicants.length > 1 ? 'Complete required tags and join questions for each person included in this membership.' : 'Complete your join details.'}
          </p>

          <div style={{
        display: 'grid',
        gap: '0.75rem',
        marginTop: '0.75rem'
      }}>
            {targetApplicants.map(target => <div key={`target-${target.uid}`} style={{
          border: targetApplicants.length > 1 ? '1px solid #e5e7eb' : 'none',
          borderRadius: targetApplicants.length > 1 ? '8px' : 0,
          padding: targetApplicants.length > 1 ? '0.75rem' : 0
        }}>
                {targetApplicants.length > 1 && <div style={{
            marginBottom: '0.5rem'
          }}>
                    <strong>{target.displayName}</strong>
                    <div className="text-muted" style={{
              fontSize: '0.9rem'
            }}>
                      {target.isPayer ? 'Payer' : target.role || 'Member'}
                    </div>
                  </div>}

                {renderRequiredTagGroups(target.uid)}
                {realQuestions.map(question => renderQuestionInput(target.uid, question))}
              </div>)}
          </div>
        </div>}

      {Array.isArray(requiredJoinDocuments) && requiredJoinDocuments.length > 0 && <div className="form-group">
          <label>Required Club Documents</label>
          <p className="text-muted" style={{
        marginTop: '0.25rem'
      }}>
            Review and accept each required document before you continue.
          </p>

          <div style={{
        display: 'grid',
        gap: '0.75rem',
        marginTop: '0.75rem'
      }}>
            {requiredJoinDocuments.map(document => {
          const sourceUrl = getDocumentSourceUrl(document);
          const actionLabels = getDocumentActionLabels(document);
          const checkboxLabel = document.acceptanceLabel || `I accept ${document.title || 'this document'}.`;
          const content = <div>
                      <strong>{document.title || 'Required document'}</strong>
                      {document.description && <p className="text-muted" style={{
                marginTop: '0.5rem',
                marginBottom: 0
              }}>
                          {document.description}
                        </p>}
                    </div>;
          return <div key={document.id} style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '0.85rem'
          }}>
                  <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '0.75rem',
              alignItems: 'flex-start',
              flexWrap: 'wrap'
            }}>
                    {sourceUrl ? <a href={sourceUrl} target="_blank" rel="noopener noreferrer" style={{
                color: 'inherit',
                textDecoration: 'none',
                flex: 1,
                minWidth: 0
              }}>
                        {content}
                      </a> : <div style={{
                flex: 1,
                minWidth: 0
              }}>
                        {content}
                      </div>}
                    {sourceUrl && <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="button button-secondary">
                          {actionLabels.view}
                        </a>
                        <button type="button" className="button button-secondary" onClick={() => triggerDocumentDownload(document)}>
                          {actionLabels.download}
                        </button>
                      </div>}
                  </div>

                  <label style={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'flex-start'
            }}>
                    <input type="checkbox" checked={acceptedDocuments?.[document.id] === true} onChange={event => {
                const checked = event.target.checked;
                setAcceptedDocuments(prev => ({
                  ...prev,
                  [document.id]: checked
                }));
              }} style={{
                marginTop: '0.2rem'
              }} />
                    <span>{checkboxLabel}</span>
                  </label>
                </div>;
        })}
          </div>
        </div>}

      {(submitError || error) && <ErrorMessage message={submitError || error} />}

      {isAdminDemo && <p className="text-muted" style={{
      marginTop: '0.25rem'
    }}>
          Demo mode: submission is disabled.
        </p>}

      <div className="form-actions flex justify-end">
        <SubmitButton label={isAdminDemo ? 'Demo Mode (Read-only)' : 'Next: Payment'} primary disabled={isAdminDemo} />
      </div>
    </form>
    {emergencyContactModalOpen && user?.uid && <EmergencyContactModal userId={user.uid} onClose={() => setEmergencyContactModalOpen(false)} onSaved={async () => {
      const hasContacts = await checkOwnEmergencyContacts({
        openModal: false
      });
      if (hasContacts) {
        setEmergencyContactModalOpen(false);
      }
    }} />}
    {syncDetailsModalOpen && user?.uid && <ParentEmergencySyncDetailsModal userId={user.uid} onClose={() => setSyncDetailsModalOpen(false)} onSaved={() => {
      setSyncDetailsModalOpen(false);
    }} />}
  </>;
}
